// @flow
import React from 'react';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { KeywordTags } from 'UI/components/molecules/KeywordTags';
import InfoLabelsGrid from 'UI/components/organisms/inventoryProfiles/InfoLabelsGrid';
import InventoryOverviewLayout from 'UI/components/organisms/inventoryProfiles/InventoryOverviewLayout';
import { AutoWidthGrid } from 'UI/constants/dimensions';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import {
  currencyFormatter,
  getFeatureFlags,
  nestTernary,
  numberFormatter,
  phoneNumberFormatter
} from 'UI/utils';
import { buildLocationInfoLabel, buildPilLabels } from 'UI/utils/inventory';

import { buildKeywordTagsCard, getPhonesLabelsForProfile } from '../utils';

type CompanyOverviewProps = {
  profile?: Object,
  loading: boolean
};

const getInfoLabelsForProfile = profile => {
  const pilLabels =
    profile &&
    buildPilLabels({
      specialty: profile?.specialty,
      additionalSpecialties: profile?.specialties,
      subspecialty: profile?.subspecialty,
      additionalSubspecialties: profile?.subspecialties
    });

  const legacyPhoneLabels = [
    {
      title: 'Phone · EXT',
      description: ` ext: ${profile?.contact?.ext ?? ''} `,
      mode: 'ringCentral',
      linkProps: {
        url: phoneNumberFormatter(profile?.contact?.phone) ?? '',
        id: profile?.id,
        role: EntityType.Company,
        customLinkProps: {
          profileName: profile?.name
        }
      }
    }
  ];

  const isMultiplePhonesEnabled = getFeatureFlags().includes(FeatureFlags.CompaniesMultiplePhones);
  const hasMultiplePhones = !!profile.phone_id;

  const phonesLabels = !isMultiplePhonesEnabled
    ? legacyPhoneLabels
    : nestTernary(
        hasMultiplePhones,
        getPhonesLabelsForProfile(profile, EntityType.Company),
        legacyPhoneLabels
      );

  const hasDescription = Boolean(profile?.description?.trim());

  const labels = [
    {
      title: 'Description',
      description: profile?.description ?? '',
      config: { xs: 12, sm: 12, md: 12 },
      visible: hasDescription
    },
    {
      title: 'Legal Name',
      description: profile?.legal_name ?? ''
    },
    {
      title: 'Number of Employees',
      description: numberFormatter(profile?.employees) ?? ''
    },
    {
      title: 'Revenue',
      description: currencyFormatter(profile?.revenue) ?? ''
    },
    {
      title: 'Industries',
      description: pilLabels?.industries
    },
    {
      title: 'Specialties',
      description: pilLabels?.specialties
    },
    {
      title: 'Subspecialties',
      description: pilLabels?.subspecialties
    },
    buildLocationInfoLabel({
      city: profile?.address?.city?.title,
      state: profile?.address?.city?.state?.slug,
      country: profile?.address?.city?.state?.country?.slug
    }),
    {
      title: 'Address',
      description: profile?.address?.address
    },
    {
      title: 'ZIP CODE',
      description: profile?.address?.zip
    },
    {
      title: 'Email',
      mode: 'externalLink',
      href: `mailto:${profile?.email}`,
      linkProps: {
        url: profile?.email ?? ''
      }
    },
    {
      title: 'Website',
      description: profile?.website,
      mode: 'externalLink',
      linkProps: {
        url: profile?.website ?? ''
      },
      config: AutoWidthGrid
    },
    {
      title: 'LinkedIn Url',
      mode: 'externalLink',
      linkProps: {
        url: profile?.link_profile ?? ''
      }
    },
    {
      title: 'Accounts Payable Email',
      mode: 'externalLink',
      href: `mailto:${profile?.accounts_payable_email}`,
      linkProps: {
        url: profile?.accounts_payable_email ?? '',
        isLinkAnEmail: true
      }
    },
    ...phonesLabels
  ];

  return labels;
};

const CompanyOverview = ({ loading, profile }: CompanyOverviewProps) => {
  const generalLabels = getInfoLabelsForProfile(profile);
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const shouldShowKeywordTags = checkIfFeatureFlagEnabled(FeatureFlags.CandidateKeywordTags);

  const cards = [
    ...buildKeywordTagsCard({
      Component: KeywordTags,
      keywordTags: profile?.keywordTags,
      isEnabled: shouldShowKeywordTags
    }),
    {
      cardProps: { headerProps: { title: 'General Information' } },
      Component: InfoLabelsGrid,
      componentProps: { labels: generalLabels, isFlexibleGrid: false },
      className: 'generalCard'
    }
  ];

  return <InventoryOverviewLayout loading={loading} items={cards} />;
};

CompanyOverview.defaultProps = {
  profile: {}
};

export default CompanyOverview;
